import { ThemeProvider } from '@emotion/react';

import { NextComponentType } from 'next';
import { SSRConfig, appWithTranslation, useTranslation } from 'next-i18next';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import App from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { startTransition, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  DehydratedState,
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from 'react-query';

import '@styles/reset.css';

import '@styles/app.css';

import { useModal } from '@hooks/useModal';
import { captureException, setTag } from '@sentry/nextjs';
import { ABTestData, useABTestStore } from '@stores/useABTestStore';
import { useChargeStore } from '@stores/useChargeStore';
import { useOverlayStore } from '@stores/useOverlayStore';
import { usePrevUrlStore } from '@stores/usePrevUrlStore';
import { useThemeStore } from '@stores/useThemeStore';
import camelize from 'camelize';
import {
  ChatieTheme,
  ChemingTheme,
  DarkTheme,
} from 'chatie-design-system/theme';
import { getCookies, setCookie } from 'cookies-next';
import { shallow } from 'zustand/shallow';

import AdBlockDetector from '@components/common/AdBlockDetector';
import ErrorBoundaryFallback from '@components/common/Error';
import GlobalToast from '@components/common/GlobalToast';
import { PageLoading } from '@components/common/Loading';
import { Maintenance } from '@components/common/Maintenance';
import BottomTab from '@components/layout/BottomTab';
import BottomTabChatie from '@components/layout/BottomTabChatie';
import ActionSheetModal from '@components/modal/ActionSheetModal';
import ChargeBalloonModal from '@components/modal/ChargeBalloonModal';

import chemingOgImg from '@public/images/og/cheming_og.png';

import nextI18NextConfig from '@base/next-i18next.config.js';
import {
  CHATIE_SERVICE_STATE,
  CHEMING_SERVICE_STATE,
  NAV_BAR_PATH,
  Platform,
} from '@constants';
import { AuthProvider } from '@contexts/auth';
import { GlobalProvider } from '@contexts/global';
import { GlobalStyle } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import { useChatieErrorHandler } from '@utils/error';
import { getAppInfo, isEnv } from '@utils/misc';
import { OpenGraphProps } from '@utils/ssr';
import { AppInterface, WebInterface, useWebView } from '@utils/webview';

import '../lib/preloadOgImages';

const isStaging = process.env.NEXT_PUBLIC_ENV === 'staging';
const isProduction = process.env.NEXT_PUBLIC_ENV === 'production';

const release = process.env.NEXT_PUBLIC_RELEASE;

const assetPrefix = isProduction
  ? `https://mn.chatie.me/assets/${release}`
  : isStaging
  ? `https://staging-mn.chatie.me/assets/${release}`
  : '';

const chemingDns = process.env.NEXT_PUBLIC_CHEMING_DNS_NUMBER;
const chemingNaverDns = process.env.NEXT_PUBLIC_CHEMING_NAVER_DNS_NUMBER;

type CustomPageProps = {
  platform: Platform;
  version: string;
  os: string | undefined;
  device: string | undefined;
  isDarkMode: boolean;
  dehydratedState?: DehydratedState;
  og: OpenGraphProps['og'];
  isCheming: boolean;
  showNavbar: boolean;
};

type CustomAppProps = AppProps<SSRConfig & CustomPageProps>;

const MyApp: NextComponentType<AppContext, AppInitialProps, CustomAppProps> = ({
  Component,
  pageProps,
}) => {
  const { t } = useTranslation();
  const { isNeedCharge, reset } = useChargeStore();

  const {
    platform: _platform,
    version: _version,
    device: _device,
    os: _os,
    dehydratedState,
    og,
    showNavbar,
    isCheming: _isCheming,
  } = pageProps;

  const [
    isChargeBalloonModalOpen,
    { open: openChargeBalloonModal, close: closeChargeBalloonModal },
  ] = useModal('charge-balloon-modal');

  const onError = useChatieErrorHandler();

  const [popOverlay] = useOverlayStore((state) => [state.pop], shallow);

  const { theme, hydrate: hydrateTheme } = useThemeStore();
  const { listen } = useWebView();
  const router = useRouter();

  const { setPrevUrl } = usePrevUrlStore();
  const { firebase } = useAnalytics();

  const [platform] = useState(_platform);
  const [isCheming] = useState(_isCheming);
  const [version] = useState(_version);
  const [device] = useState(_device);
  const [os] = useState(_os);
  const [isPageLoading, setPageLoading] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const { goodsName, serviceName, chatName } = isCheming
    ? CHEMING_SERVICE_STATE
    : CHATIE_SERVICE_STATE;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            suspense: true,
            retry: 0,
          },
          mutations: {
            onError,
          },
        },
      })
  );

  // const handleDomain = () => {
  //   setIsCheming(!isCheming);
  // };

  // hydrate theme from localStorage after mount (SSR-safe)
  // + 탭 전환(visibilitychange) 시 다른 WebView에서 변경된 테마 동기화
  useEffect(() => {
    hydrateTheme();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        hydrateTheme();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hydrateTheme]);

  // 테마 변경 시 body/html 스타일 직접 적용
  // Emotion Global이 Android WebView에서 갱신 안 되는 이슈 우회
  // 외부 SDK 페이지는 다크모드 미적용
  const isDarkExcluded =
    [
      '/studio/stories/[storyId]/episodes/[episodeId]/edit',
      '/studio/stories/[storyId]/episodes/[episodeId]/preview',
      '/stories/[storyId]/episodes/[episodeId]',
      '/stories/[storyId]/episodes/[episodeId]/licensed',
    ].includes(router.pathname) ||
    router.pathname.startsWith('/play/chat-rooms/');

  useEffect(() => {
    const effectiveTheme =
      theme === 'dark' && !isDarkExcluded ? 'dark' : 'light';
    const colors =
      effectiveTheme === 'dark'
        ? DarkTheme
        : isCheming
        ? ChemingTheme
        : ChatieTheme;
    document.documentElement.dataset.theme = effectiveTheme;
    document.documentElement.style.backgroundColor = colors.var.white;
    document.body.style.backgroundColor = colors.var.white;
    document.body.style.color = colors.var.black;
  }, [theme, isCheming, isDarkExcluded]);

  // 테마 변경 시 네이티브 앱 상태바/네비게이션바/하단탭 색상 동기화
  // hydration 전 기본값('light')으로 'white' 보내는 것 방지 → 깜빡임 제거
  const prevThemeRef = useRef<string | null>(null);

  useEffect(() => {
    if (platform === Platform.web) return;
    if (prevThemeRef.current === null) {
      prevThemeRef.current = theme;
      return;
    }
    const web = WebInterface?.getInstance();
    web?.setStatusBarColor(theme === 'dark' ? 'black' : 'white');
    prevThemeRef.current = theme;
  }, [theme, platform]);

  // interface for app webview javascript
  useEffect(() => {
    window.chatieInterface = {
      web: WebInterface.getInstance(),
      app: AppInterface.getInstance(),
    };
    window.ci = window.chatieInterface;

    window.ci.web.initialized();

    setTag('version', version);

    // navDepth 추적: pushState 인터셉트
    const { pushNav, popNav } = usePrevUrlStore.getState();

    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      pushNav();
      return originalPushState.apply(this, args);
    };

    const onPopState = () => {
      popOverlay();
      popNav();
    };
    window.addEventListener('popstate', onPopState);

    const onRouteChangeStart = (
      url: string,
      { shallow }: { shallow: boolean }
    ) => {
      setPrevUrl(url);
      if (!shallow) {
        setPageLoading(true);
      }
    };
    const onRouteChangeEnd = () => {
      setPageLoading(false);
    };

    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeEnd);
    router.events.on('routeChangeError', onRouteChangeEnd);

    return () => {
      window.history.pushState = originalPushState;
      window.removeEventListener('popstate', onPopState);
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteChangeEnd);
      router.events.off('routeChangeError', onRouteChangeEnd);
    };
  }, []);

  const { setABTestData } = useABTestStore();

  useEffect(() => {
    const unlisten = listen('transferABTestData', (e) => {
      const { data } = e.detail;
      setABTestData(camelize(data) as ABTestData);
    });

    return unlisten;
  }, []);

  useEffect(() => {
    if (isNeedCharge) {
      firebase.logEvent('open_payment_modal', {
        group: 'B',
      });
      openChargeBalloonModal();

      reset();
    }
  }, [isNeedCharge]);

  useEffect(() => {
    startTransition(() => {
      const checkIfTablet = () => {
        const width = window.innerWidth;
        setIsTablet(width >= 768 && width <= 1024);
      };
      checkIfTablet();
    });
  }, []);

  return (
    <>
      <Head>
        <link
          rel="preconnect"
          href={process.env.NEXT_PUBLIC_EFINDER_CDN_URL}
          crossOrigin="anonymous"
        />

        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, user-scalable=no, minimum-scale=1.0, maximum-scale=1.0, viewport-fit=cover"
        />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-title"
          content={isCheming ? '케밍' : 'Chatie'}
        />
        {isCheming && (
          <>
            <meta name="google-site-verification" content={chemingDns} />
            <meta name="naver-site-verification" content={chemingNaverDns} />
            <meta
              name="robots"
              content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
            />

            <meta property="og:locale" content="ko_KR" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="케밍" />
            <meta name="twitter:card" content="summary_large_image" />
            <link rel="canonical" href={`https://cheming.ai${router.asPath}`} />
            <meta
              name="keywords"
              content="케밍, 제타, 재타, ai 채팅, 뤼튼, 러비더비, 채티, 체티, 캐릭터 ai, 뤼튼 ai, ai 여자친구, ai 남자친구, ai 캐릭터 만들기, 가상 친구, 웹소설 채팅, ai 채팅 친구,로판 ai"
            />
            <meta name="format-detection" content="telephone=no" />
          </>
        )}

        {!isCheming && (
          <>
            <meta
              name="robots"
              content={
                isProduction
                  ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
                  : 'noindex, nofollow'
              }
            />
            <link rel="canonical" href={`https://chatie.me${router.asPath}`} />
            <meta name="format-detection" content="telephone=no" />
          </>
        )}

        {isCheming && !isProduction && (
          <meta name="robots" content="noindex"></meta>
        )}
        {isCheming && (
          <>
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href="/cheming-apple-touch-icon.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href="/cheming-favicon-32x32.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="/cheming-favicon-16x16.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="/cheming-favicon-96x96.png"
            />
          </>
        )}
        {/* <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#20a2ae" /> */}
        <link
          rel="icon"
          href={
            isCheming
              ? '/cheming-favicon.ico'
              : assetPrefix + '/_next/static/media/favicon.ico'
          }
          type="image/x-icon"
        />
        {!isCheming && <link rel="manifest" href="/site.webmanifest" />}
        {isCheming && <link rel="manifest" href="/cheming-site.webmanifest" />}
        <meta name="msapplication-TileColor" content="#20a2ae" />
        <meta
          name="theme-color"
          content={theme === 'dark' ? '#1c1d22' : '#ffffff'}
        />

        {og?.title ? (
          <title>{`${og.title}${t(isCheming ? '' : ' - 채티')}`}</title>
        ) : (
          <title>
            {t(
              isCheming
                ? '케밍 - 캐릭터와 케미가 터지는 순간!'
                : '채티 - 채팅형 소설 읽기, 쓰기'
            )}
          </title>
        )}
        {og?.image && (
          <>
            <meta name="og:image" property="og:image" content={og.image.url} />
            <meta
              property="og:image:width"
              content={og.image.width.toString()}
            />
            <meta
              property="og:image:height"
              content={og.image.height.toString()}
            />
          </>
        )}

        {isCheming && !og?.image && chemingOgImg?.src && (
          <>
            <meta
              name="og:image"
              property="og:image"
              content={chemingOgImg?.src}
            />
            <meta property="og:image:width" content={'1200'} />
            <meta property="og:image:height" content={'630'} />
          </>
        )}
        {og?.description && (
          <>
            <meta property="og:description" content={og.description} />
            <meta name="description" content={og.description} />
          </>
        )}
        {isCheming && !og?.description && (
          <>
            <meta
              name="description"
              content={
                '단계별로 깊어지는 대화, 캐릭터와의 케미를 경험해보세요.'
              }
            />
            <meta
              property="og:description"
              content={
                '단계별로 깊어지는 대화, 캐릭터와의 케미를 경험해보세요.'
              }
            />
          </>
        )}
        {!isCheming && !og?.description && (
          <>
            <meta
              name="description"
              content="채팅형 소설을 읽고 직접 연재할 수 있는 채티에서 로맨스, 판타지, 스릴러, 팬픽 등 다양한 작품을 만나보세요."
            />
            <meta
              property="og:description"
              content="채팅형 소설을 읽고 직접 연재할 수 있는 채티에서 로맨스, 판타지, 스릴러, 팬픽 등 다양한 작품을 만나보세요."
            />
          </>
        )}
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehydratedState}>
          <ThemeProvider
            theme={
              theme === 'dark' && !isDarkExcluded
                ? DarkTheme
                : isCheming
                ? ChemingTheme
                : ChatieTheme
            }
          >
            <GlobalStyle platform={platform} />
            <GlobalProvider
              value={{
                platform,
                version,
                device,
                os,
                isTablet,
                isCheming,
                goodsName,
                serviceName,
                chatName,
              }}
            >
              <AuthProvider>
                <AdBlockDetector
                  continuous={false} // 한 번만 체크
                >
                  <ErrorBoundary
                    key={router.asPath}
                    FallbackComponent={ErrorBoundaryFallback}
                    onError={(error, errorInfo) => {
                      console.error(error);
                      captureException(error, { extra: errorInfo });
                    }}
                  >
                    <Component {...pageProps} />
                    <ChargeBalloonModal
                      open={isChargeBalloonModalOpen}
                      isCheming={isCheming}
                      onClose={closeChargeBalloonModal}
                    />
                    {showNavbar && <BottomTab />}
                    {!isCheming && <BottomTabChatie />}
                    <GlobalToast />
                  </ErrorBoundary>
                </AdBlockDetector>
              </AuthProvider>
            </GlobalProvider>
            <ActionSheetModal depth={100} />
            {isPageLoading && <PageLoading />}
          </ThemeProvider>
          {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          {/* {isEnv('dev') && (
            <FloatingButton top={0} onClick={handleDomain}>
              <div>{t(isCheming ? '채티전환' : '케밍전환')}</div>
            </FloatingButton>
          )} */}
        </Hydrate>

        <Maintenance />
      </QueryClientProvider>
    </>
  );
};

const staticPages = ['/404'];

MyApp.getInitialProps = async (
  appContext: AppContext
): Promise<AppInitialProps> => {
  const { Component, ctx, router } = appContext;
  const appProps = await App.getInitialProps(appContext);
  const CHEMING_DOMAIN = process.env.NEXT_PUBLIC_CHEMING_DOMAIN;
  let hostname = '';

  if (appContext.ctx.req) {
    hostname = appContext.ctx.req.headers.host ?? '';
  } else if (typeof window !== 'undefined') {
    hostname = window.location.host;
  }
  const chemingHost = CHEMING_DOMAIN ? new URL(CHEMING_DOMAIN).host : '';
  const isCheming = hostname === chemingHost;

  const pathname = appContext.ctx.pathname;
  const showNavbar = NAV_BAR_PATH.includes(pathname) && isCheming;
  const pageProps = Component.getInitialProps
    ? await Component.getInitialProps(ctx)
    : {};
  const cookies = getCookies(ctx);

  if (ctx.req && !staticPages.includes(ctx.pathname)) {
    const options = {
      ...ctx,
      domain: isEnv('dev') ? undefined : isCheming ? 'cheming.ai' : 'chatie.me',
    };

    if (cookies.accessToken) {
      setCookie('accessToken', cookies.accessToken, options);
    }
    if (cookies.firebaseIdToken) {
      setCookie('firebaseIdToken', cookies.firebaseIdToken, options);
    }
  }
  const device = ctx.req ? (ctx.req.headers['x-device'] as string) : undefined;
  const os = ctx.req ? (ctx.req.headers['x-os'] as string) : undefined;
  const userAgent = ctx.req
    ? ctx.req.headers['user-agent']
    : navigator.userAgent;

  const isDarkMode = userAgent?.match(/dark-mode/) ? true : false;
  const [platform, version] = getAppInfo(userAgent);

  global.__localeId__ = router.locale ?? 'ko';
  global.__darkMode__ = isDarkMode;

  return {
    ...appProps,
    pageProps: {
      ...pageProps,
      isCheming,
      isDarkMode,
      platform,
      showNavbar,
      version,
      device,
      os,
    },
  };
};

export default appWithTranslation(MyApp, nextI18NextConfig);
